import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { canSell, isOrgManager } from '../utils/roles';

// Confirmation prompt when manually moving a deal to Sold or Closed. Captures the sold price + close
// date and sends them with the stage change (PUT /api/deals/:id/stage). Feeds the Pipeline
// Sold/Closed count and the period-filtered Assignment-Fees math.
//
// Selling an AVAILABLE deal is also how the sales side takes it on: whoever confirms becomes the deal's
// sales agent and its co-owner (routes/deals.js PUT /:id/stage). So for that move the modal also asks
// which buyer it went to, and lets an owner/admin record the sale on a sales agent's behalf.
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function SoldCloseModal({ dealId, stage, deal, onClose, onConfirmed }) {
  const [salePrice, setSalePrice] = useState(
    // Our sell price first: asking_price is what the SELLER listed at (migration 076).
    deal?.sale_price != null ? String(deal.sale_price)
      : deal?.marketing_price != null ? String(deal.marketing_price)
      : (deal?.asking_price != null ? String(deal.asking_price) : ''),
  );
  const [closeDate, setCloseDate] = useState(deal?.close_date ? String(deal.close_date).slice(0, 10) : todayISO());
  const [saving, setSaving] = useState(false);
  const { role, user } = useAuth();
  const isSale = stage === 'Sold' && deal?.stage === 'Available';
  const manager = isOrgManager(role);
  const [buyers, setBuyers] = useState([]);
  const [buyerId, setBuyerId] = useState(deal?.assignee_buyer_id ? String(deal.assignee_buyer_id) : '');
  const [sellers, setSellers] = useState([]);
  const [dispoId, setDispoId] = useState(user?.id ? String(user.id) : '');

  useEffect(() => {
    if (!isSale) return;
    // Only buyers this person can see — the server checks the pick against the same rule.
    fetch('/api/buyers?limit=5300', { credentials: 'include' }).then(r => (r.ok ? r.json() : []))
      .then(d => setBuyers(Array.isArray(d) ? d : (Array.isArray(d?.buyers) ? d.buyers : [])))
      .catch(() => {});
    if (manager) {
      fetch('/api/deals/org-users', { credentials: 'include' }).then(r => (r.ok ? r.json() : []))
        .then(d => setSellers((Array.isArray(d) ? d : []).filter(u => canSell(u.role))))
        .catch(() => {});
    }
  }, [isSale, manager]);

  async function confirm() {
    setSaving(true);
    try {
      const r = await fetch(`/api/deals/${dealId}/stage`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stage, sale_price: salePrice === '' ? null : Number(salePrice), close_date: closeDate || null,
          ...(isSale ? { assignee_buyer_id: buyerId || null, ...(manager && dispoId ? { dispo_user_id: Number(dispoId) } : {}) } : {}),
        }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      onConfirmed(stage);
    } catch (e) { alert(`Failed: ${e.message}`); setSaving(false); }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--navy-border)' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>Mark as {stage}</div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3 }}>
            {deal?.address ? `${deal.address} · ` : ''}Confirm the sold price and close date.
          </div>
        </div>
        <div style={{ padding: 20 }}>
          <label style={labelStyle}>Sold Price</label>
          <div style={{ position: 'relative', marginBottom: 16 }}>
            <span style={{ position: 'absolute', left: 12, top: 10, color: 'var(--gray-500)', fontSize: 14 }}>$</span>
            <input type="number" value={salePrice} onChange={e => setSalePrice(e.target.value)} placeholder="0"
              style={{ ...inputStyle, paddingLeft: 24 }} />
          </div>
          <label style={labelStyle}>Close Date</label>
          <input type="date" value={closeDate} onChange={e => setCloseDate(e.target.value)} style={inputStyle} />
          {isSale && (
            <>
              <label style={{ ...labelStyle, marginTop: 16 }}>End Buyer</label>
              <select value={buyerId} onChange={e => setBuyerId(e.target.value)} style={inputStyle}>
                <option value="">— Not recorded —</option>
                {buyers.map(b => (
                  <option key={b.id} value={b.id}>{[b.name, b.company].filter(Boolean).join(' · ') || b.email || `Buyer ${b.id}`}</option>
                ))}
              </select>
              {manager && (
                <>
                  <label style={{ ...labelStyle, marginTop: 16 }}>Sold By</label>
                  <select value={dispoId} onChange={e => setDispoId(e.target.value)} style={inputStyle}>
                    {sellers.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                  </select>
                </>
              )}
              <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 12, lineHeight: 1.5 }}>
                {manager ? 'The person who sold it' : 'You'} become{manager ? 's' : ''} this deal's sales agent: co-owner with the
                acquisitions agent, credited with the full unit and fee.
              </div>
            </>
          )}
        </div>
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--navy-border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <button onClick={confirm} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.5 : 1, cursor: saving ? 'default' : 'pointer' }}>
            {saving ? 'Saving…' : `Confirm ${stage}`}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay   = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const panel     = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, width: 'min(420px, 100%)', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' };
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 14 };
const ghostBtn   = { padding: '8px 16px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const primaryBtn = { padding: '8px 18px', borderRadius: 8, background: 'var(--teal)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700 };
